import { createInitialStaffOpsState } from "./mock-data.ts";
import type {
  AnalyticsEvent,
  AuditEntry,
  GiftRecord,
  GiftStatus,
  Incident,
  ModerationReport,
  ModerationStatus,
  OrderRecord,
  OrderStatus,
  PaymentRecord,
  StaffOpsState,
  StaffPrincipal,
  VenueBan,
  VenueSessionStatus,
} from "./types.ts";

let state: StaffOpsState = createInitialStaffOpsState();
let auditCounter = 100;
let venueBans: VenueBan[] = [];
let banCounter = 1;

const sessionTransitions: Record<VenueSessionStatus, VenueSessionStatus | null> = {
  scheduled: "open",
  open: "last_call",
  last_call: "closing",
  closing: "closed",
  closed: null,
};

const orderNext: Record<OrderStatus, OrderStatus[]> = {
  pending: ["accepted", "rejected", "cancelled"],
  accepted: ["preparing", "cancelled"],
  preparing: ["ready", "cancelled"],
  ready: ["served"],
  served: [],
  cancelled: [],
  rejected: [],
};

const reportActions: Record<string, ModerationStatus> = {
  triage: "triaged",
  warn: "actioned",
  mute_social: "actioned",
  disable_social: "actioned",
  ban: "actioned",
  unban: "actioned",
  dismiss: "dismissed",
  escalate: "escalated",
};

function clone<T>(value: T): T {
  return structuredClone(value) as T;
}

function stamp(): string {
  return new Date().toISOString();
}

function audit(
  action: string,
  entityType: string,
  entityId: string,
  before: string | undefined,
  after: string,
  reason?: string,
): AuditEntry {
  const entry: AuditEntry = {
    id: `aud-${auditCounter++}`,
    actor: "demo-staff",
    action,
    entityType,
    entityId,
    before,
    after,
    reason,
    createdAt: stamp(),
  };
  state.auditLog.unshift(entry);
  return entry;
}

function event(name: AnalyticsEvent["name"], payload: AnalyticsEvent["payload"]): AnalyticsEvent {
  const record = {
    id: `aev-${state.analyticsEvents.length + 1}`,
    name,
    payload,
    createdAt: stamp(),
  };
  state.analyticsEvents.unshift(record);
  return record;
}

export function resetStaffOpsMockState(): StaffOpsState {
  state = createInitialStaffOpsState();
  auditCounter = 100;
  venueBans = [];
  banCounter = 1;
  return getStaffOpsState();
}

export function getStaffOpsState(): StaffOpsState {
  return clone(state);
}

export function loginStaff(staffCode: string, pin: string): StaffPrincipal | null {
  if (!pin || pin.length < 4) return null;
  return clone(
    state.staffUsers.find((user) => user.staffCode.toLowerCase() === staffCode.toLowerCase()) ??
      state.staffUsers[4] ??
      null,
  );
}

export function listOrders(station = "all"): OrderRecord[] {
  return clone(
    station === "all" ? state.orders : state.orders.filter((order) => order.station === station),
  );
}

export function getOrder(orderId: string): OrderRecord | null {
  return clone(state.orders.find((order) => order.id === orderId) ?? null);
}

export function updateOrderStatus(
  orderId: string,
  nextStatus: OrderStatus,
  reason?: string,
): OrderRecord {
  const order = state.orders.find((item) => item.id === orderId);
  if (!order) throw new Error("ORDER_NOT_FOUND");
  if ((nextStatus === "cancelled" || nextStatus === "rejected") && !reason?.trim()) {
    throw new Error("REASON_REQUIRED");
  }
  if (!orderNext[order.status].includes(nextStatus)) {
    order.conflict = `Cannot transition ${order.status} to ${nextStatus}`;
    throw new Error("INVALID_ORDER_TRANSITION");
  }
  const before = order.status;
  order.status = nextStatus;
  order.updatedAt = stamp();
  order.conflict = undefined;
  const entry = audit(`order_${nextStatus}`, "Order", order.id, before, nextStatus, reason);
  order.timeline.unshift(entry);
  if (nextStatus === "accepted")
    event("AEV-order_accepted", { orderId, station: order.station, source: order.source });
  if (nextStatus === "served")
    event("AEV-order_served", { orderId, tableSessionId: order.tableSessionId });
  return clone(order);
}

export function listGifts(includeAll = false): GiftRecord[] {
  return clone(includeAll ? state.gifts : state.gifts.filter((gift) => gift.status === "accepted"));
}

export function getGift(giftId: string): GiftRecord | null {
  return clone(state.gifts.find((gift) => gift.id === giftId) ?? null);
}

export function updateGiftStatus(
  giftId: string,
  nextStatus: Extract<GiftStatus, "fulfilled" | "staff_rejected">,
  reason?: string,
): GiftRecord {
  const gift = state.gifts.find((item) => item.id === giftId);
  if (!gift) throw new Error("GIFT_NOT_FOUND");
  if (gift.status !== "accepted") throw new Error("GIFT_NOT_ACCEPTED");
  if (nextStatus === "staff_rejected" && !reason?.trim()) throw new Error("REASON_REQUIRED");
  const before = gift.status;
  gift.status = nextStatus;
  if (nextStatus === "fulfilled") {
    gift.fulfilledAt = stamp();
    event("AEV-gift_fulfilled", { giftId, alcohol: gift.alcohol, itemCount: gift.items.length });
  } else {
    gift.refundState = "refund_pending";
    const payment = state.payments.find((item) => item.id === gift.paymentId);
    if (payment) payment.status = "refund_pending";
  }
  audit(
    nextStatus === "fulfilled" ? "gift_fulfilled" : "gift_staff_rejected",
    "Gift",
    gift.id,
    before,
    nextStatus,
    reason,
  );
  return clone(gift);
}

export function listTables(): StaffOpsState["tables"] {
  return clone(state.tables);
}

export function forceClearTable(
  tableSessionId: string,
  reason: string,
): StaffOpsState["tables"][number] {
  const table = state.tables.find((item) => item.id === tableSessionId);
  if (!table) throw new Error("TABLE_SESSION_NOT_FOUND");
  const before = table.status;
  table.status = "free";
  table.partySize = 0;
  table.socialActive = false;
  table.auditNote = reason;
  audit("table_force_cleared", "TableSession", table.id, before, "free", reason);
  return clone(table);
}

export function transitionVenueSession(): StaffOpsState["session"] {
  const next = sessionTransitions[state.session.status];
  if (!next) throw new Error("SESSION_TERMINAL");
  const before = state.session.status;
  state.session.status = next;
  if (next === "open") state.session.openedAt = stamp();
  if (next === "last_call") state.session.lastCallAt = stamp();
  audit("venue_session_transition", "VenueSession", state.session.id, before, next);
  return clone(state.session);
}

export function setSocialPaused(paused: boolean, reason?: string): StaffOpsState["session"] {
  const before = state.session.socialPaused ? "paused" : "enabled";
  state.session.socialPaused = paused;
  if (paused) {
    for (const gift of state.gifts) {
      if (gift.status === "pending") gift.status = "voided";
    }
  }
  // FR-STAFF-093 / SAFE-012: engaging the kill switch auto-creates an Incident.
  // Create it (and its own audit entry) before the primary audit entry below,
  // so the most recent audit row always reflects the action the caller took.
  if (paused) {
    createIncident({
      title: "Emergency social disable",
      severity: "high",
      description: reason ?? "Emergency social disable triggered",
    });
  }
  audit(
    paused ? "social_paused" : "social_resumed",
    "VenueSession",
    state.session.id,
    before,
    paused ? "paused" : "enabled",
    reason,
  );
  event("AEV-social_paused", { sessionId: state.session.id, paused });
  return clone(state.session);
}

export function toggleConnection(kind: "degraded" | "offline"): StaffOpsState["connection"] {
  state.connection[kind] = !state.connection[kind];
  if (kind === "offline" && state.connection.offline) state.connection.degraded = true;
  return clone(state.connection);
}

export function listReports(): ModerationReport[] {
  const weight = { critical: 0, high: 1, medium: 2, low: 3 } as const;
  return clone(
    [...state.reports].sort(
      (a, b) =>
        weight[a.severity] - weight[b.severity] || b.submittedAt.localeCompare(a.submittedAt),
    ),
  );
}

export function getReport(reportId: string): ModerationReport | null {
  return clone(state.reports.find((report) => report.id === reportId) ?? null);
}

export function actionReport(
  reportId: string,
  action: keyof typeof reportActions,
  reason?: string,
): ModerationReport {
  const report = state.reports.find((item) => item.id === reportId);
  if (!report) throw new Error("REPORT_NOT_FOUND");
  // SAFE-011 / FR-STAFF-081: a ban is a high-consequence action against a
  // real person and always requires a staff-entered reason.
  if (action === "ban" && !reason?.trim()) throw new Error("REASON_REQUIRED");
  const before = report.status;
  const nextStatus = reportActions[action];
  if (!nextStatus) throw new Error("UNKNOWN_MODERATION_ACTION");
  report.status = nextStatus;
  const entry = audit(
    `moderation_${action}`,
    "ModerationReport",
    report.id,
    before,
    report.status,
    reason,
  );
  report.history.unshift(entry);

  if (action === "ban") {
    const ban: VenueBan = {
      id: `ban-${banCounter++}`,
      lineUserId: report.targetLabel,
      reason,
      bannedAt: stamp(),
      reportId,
    };
    venueBans.unshift(ban);
  }
  if (action === "unban") {
    venueBans = venueBans.filter((ban) => ban.reportId !== reportId);
  }
  // SAFE-031: escalating a report opens a linked Incident.
  if (action === "escalate") {
    createIncident({
      title: `Escalated report: ${report.reason}`,
      severity: report.severity,
      description: reason ?? "Report escalated from moderation queue",
      reportId,
    });
  }

  return clone(report);
}

export function listVenueBans(): VenueBan[] {
  return clone(venueBans);
}

export function unbanUser(banId: string): void {
  const before = venueBans.find((ban) => ban.id === banId);
  if (!before) throw new Error("BAN_NOT_FOUND");
  venueBans = venueBans.filter((ban) => ban.id !== banId);
  audit("venue_unban", "VenueBan", banId, "active", "lifted");
}

export function createIncident(input: {
  title: string;
  severity: Incident["severity"];
  description: string;
  reportId?: string;
}): Incident {
  const incident: Incident = {
    id: `inc-${state.incidents.length + 1}`,
    reportId: input.reportId,
    title: input.title,
    severity: input.severity,
    status: "open",
    createdAt: stamp(),
    summary: input.description,
    notes: [],
  };
  state.incidents.unshift(incident);
  audit("incident_created", "Incident", incident.id, undefined, "open", input.description);
  return clone(incident);
}

export function addIncidentNote(incidentId: string, note: string): Incident {
  const incident = state.incidents.find((item) => item.id === incidentId);
  if (!incident) throw new Error("INCIDENT_NOT_FOUND");
  incident.notes.push({
    id: `inc-note-${incident.notes.length + 1}`,
    author: "demo-staff",
    note,
    createdAt: stamp(),
  });
  return clone(incident);
}

export function listIncidents(): StaffOpsState["incidents"] {
  return clone(state.incidents);
}

export function listPayments(): PaymentRecord[] {
  return clone(state.payments);
}

export function markPaid(
  paymentId: string,
  method: NonNullable<PaymentRecord["method"]>,
): PaymentRecord {
  const payment = state.payments.find((item) => item.id === paymentId);
  if (!payment) throw new Error("PAYMENT_NOT_FOUND");
  const before = payment.status;
  payment.status = "captured";
  payment.method = method;
  audit("payment_captured", "Payment", payment.id, before, "captured", method);
  return clone(payment);
}

export function requestRefund(paymentId: string, reason: string): PaymentRecord {
  if (!reason.trim()) throw new Error("REASON_REQUIRED");
  const payment = state.payments.find((item) => item.id === paymentId);
  if (!payment) throw new Error("PAYMENT_NOT_FOUND");
  const before = payment.status;
  payment.status = "refund_pending";
  payment.refundReason = reason;
  audit("refund_requested", "Payment", payment.id, before, "refund_pending", reason);
  return clone(payment);
}
